import React, { CSSProperties } from 'react';
import { sanitizeRichText, sanitizeHtml, stripHtml } from '../../../../utils/sanitizeHtml';
import { RichTextContainer } from './RichText.styled';

interface IRichTextProps {
  /** The HTML or plain text content to render */
  content: string;
  /** Whether to auto-detect plain text and convert to HTML (default: true) */
  autoConvert?: boolean;
  /** Whether to strip all HTML tags and render as plain text (default: false) */
  stripTags?: boolean;
  /** Whether to render text in bold (default: false) */
  bold?: boolean;
  /** Additional allowed HTML tags */
  extraAllowedTags?: string[];
  /** Additional allowed HTML attributes */
  extraAllowedAttrs?: string[];
  /** Custom CSS styles to override default styles */
  style?: CSSProperties;
}

/**
 * A component that safely renders rich text/HTML content from the backend.
 *
 * - Sanitizes HTML to prevent XSS attacks
 * - Auto-converts plain text to HTML paragraphs (optional)
 * - Provides consistent styling for rich text elements
 *
 * @example
 * // Render HTML from backend
 * <RichText content={booster.objective} />
 *
 * @example
 * // Render with custom class
 * <RichText content={content} className="my-custom-class" />
 */
export function RichText ({
  content,
  autoConvert = true,
  stripTags = false,
  bold = false,
  extraAllowedTags,
  extraAllowedAttrs,
  style,
}: IRichTextProps) {
  if (!content) {
    return null;
  }

  // If stripTags is true, just return plain text without any HTML
  if (stripTags) {
    const plainText = stripHtml(content);
    return (
      <RichTextContainer color="oxfordBlue" bold={bold} style={style}>
        {plainText}
      </RichTextContainer>
    );
  }

  const options = {
    extraAllowedTags,
    extraAllowedAttrs,
  };

  const sanitizedContent = autoConvert
    ? sanitizeRichText(content, options)
    : sanitizeHtml(content, options);

  return (
    <RichTextContainer
      color="textBlack"
      bold={bold}
      style={style}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
}

export default RichText;
